import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface FormState {
  name: string
  email: string
  subject: string
  message: string
}

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error'

export default function ContactPage() {
  const user = useAuthStore((s) => s.user)
  const profile = useUserStore((s) => s.profile)
  const [searchParams] = useSearchParams()

  const [form, setForm] = useState<FormState>({
    name: profile?.name ?? '',
    email: user?.email ?? '',
    subject: searchParams.get('subject') ?? '',
    message: '',
  })
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')

    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token

    const { data, error } = await supabase.functions.invoke('contact', {
      body: {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      },
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })

    if (error || !data?.id) {
      setStatus('error')
      setErrorMsg('Something went wrong. Please try again or email support@roleround.com.')
      return
    }

    setStatus('success')
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <h1 className="text-3xl font-bold mb-2">Message received</h1>
          <p className="text-muted-foreground mb-8">
            Thanks — we read every message and typically respond within 1 business day.
          </p>
          <a href="/" className="text-primary underline underline-offset-2 text-sm">
            ← Back to RoleRound
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold mb-2">Get in Touch</h1>
        <p className="text-muted-foreground text-sm mb-10">
          Questions, feedback, or bug reports — we read every message and respond to every genuine
          question.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-sm font-medium">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email <span className="text-destructive">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="subject" className="text-sm font-medium">
              Subject
            </label>
            <input
              id="subject"
              name="subject"
              type="text"
              value={form.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              maxLength={200}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="message" className="text-sm font-medium">
              Message <span className="text-destructive">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={6}
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us what's on your mind..."
              maxLength={2000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{form.message.length}/2000</p>
          </div>

          {status === 'error' && <p className="text-sm text-destructive">{errorMsg}</p>}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-6 py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? 'Sending…' : 'Send message →'}
          </button>
        </form>

        <div className="border-t mt-12 pt-8 space-y-2">
          <p className="text-sm text-muted-foreground">
            <a href="/privacy" className="text-primary underline underline-offset-2">
              Privacy Policy
            </a>
            {' · '}
            <a href="/terms" className="text-primary underline underline-offset-2">
              Terms of Service
            </a>
            {' · '}
            <a href="/" className="text-primary underline underline-offset-2">
              ← Back to RoleRound
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
